import { brand } from "../lib/brand";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { HEARING_QUESTIONS } from "../../shared/hearing-questions";
import { ErrorBanner, SuccessBanner, Loading } from "../components/ui";

const REVENUE_OPTS =
  HEARING_QUESTIONS.find((q) => q.code === "annual_revenue_band")?.options ?? [];
const EMPLOYEE_OPTS =
  HEARING_QUESTIONS.find((q) => q.code === "employee_band")?.options ?? [];

interface MeProfile {
  displayName?: string | null;
  /** 参加者名のフリガナ（任意） */
  kana?: string | null;
  companyName?: string | null;
  /** 法人名のフリガナ（任意） */
  companyNameKana?: string | null;
  phone?: string | null;
  address?: string | null;
  businessDescription?: string | null;
  annualRevenueBand?: string | null;
  employeeBand?: string | null;
  challengesText?: string | null;
  supportNeedsText?: string | null;
}

export function MyPageProfileEditPage() {
  const { me } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [f, setF] = useState<MeProfile>({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ member: MeProfile }>("/member/me");
        setF({
          displayName: r.member.displayName ?? "",
          kana: r.member.kana ?? "",
          companyName: r.member.companyName ?? "",
          companyNameKana: r.member.companyNameKana ?? "",
          phone: r.member.phone ?? "",
          address: r.member.address ?? "",
          businessDescription: r.member.businessDescription ?? "",
          annualRevenueBand: r.member.annualRevenueBand ?? "",
          employeeBand: r.member.employeeBand ?? "",
          challengesText: r.member.challengesText ?? "",
          supportNeedsText: r.member.supportNeedsText ?? "",
        });
      } catch (e) {
        setErr((e as Error).message ?? "読み込みに失敗しました");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k: keyof MeProfile, v: string) => setF((p) => ({ ...p, [k]: v }));

  if (loading) return <Loading />;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-tora-ink">会員情報の編集</h1>
        <Link to="/mypage" className="tap-target text-sm text-tora hover:underline">
          マイページへ戻る
        </Link>
      </div>
      <p className="text-sm text-tora-ink/70">
        最新の情報をご登録ください。内容は運営が最適なご縁やサポートのために活用します。
      </p>

      {err && <ErrorBanner>{err}</ErrorBanner>}
      {msg && <SuccessBanner>{msg}</SuccessBanner>}

      <form
        className="card p-5 md:p-6 space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          setMsg(null);
          if (!f.displayName?.trim()) {
            setErr("参加者名は必須です");
            return;
          }
          setBusy(true);
          try {
            await api.patch("/member/me", {
              displayName: f.displayName?.trim(),
              kana: f.kana ?? null,
              companyName: f.companyName ?? null,
              companyNameKana: f.companyNameKana ?? null,
              phone: f.phone ?? null,
              address: f.address ?? null,
              businessDescription: f.businessDescription ?? null,
              annualRevenueBand: f.annualRevenueBand || null,
              employeeBand: f.employeeBand || null,
              challengesText: f.challengesText ?? null,
              supportNeedsText: f.supportNeedsText ?? null,
            });
            setMsg("保存しました");
            setTimeout(() => nav("/mypage"), 600);
          } catch (e) {
            setErr((e as Error).message ?? "保存に失敗しました");
          } finally {
            setBusy(false);
          }
        }}
      >
        <div>
          <label className="label">法人名</label>
          <input
            className="input"
            value={f.companyName ?? ""}
            onChange={(e) => set("companyName", e.target.value)}
            maxLength={120}
            placeholder={`例: 株式会社${brand.shortName}`}
          />
        </div>
        <div>
          <label className="label">法人名フリガナ（任意）</label>
          <input
            className="input"
            value={f.companyNameKana ?? ""}
            onChange={(e) => set("companyNameKana", e.target.value)}
            maxLength={120}
            placeholder="例: カブシキガイシャ〇〇"
          />
        </div>
        <div>
          <label className="label">参加者名 *</label>
          <input
            className="input"
            value={f.displayName ?? ""}
            onChange={(e) => set("displayName", e.target.value)}
            maxLength={80}
            required
          />
        </div>
        <div>
          <label className="label">参加者名フリガナ（任意）</label>
          <input
            className="input"
            value={f.kana ?? ""}
            onChange={(e) => set("kana", e.target.value)}
            maxLength={80}
            placeholder="例: ヤマダ タロウ"
          />
        </div>
        <div>
          <label className="label">メールアドレス（変更不可）</label>
          <input
            className="input bg-black/5 text-tora-ink/60"
            value={me?.user.email ?? ""}
            disabled
            readOnly
          />
        </div>
        <div>
          <label className="label">所在地</label>
          <input
            className="input"
            value={f.address ?? ""}
            onChange={(e) => set("address", e.target.value)}
            maxLength={240}
            placeholder="例: 東京都〇〇区…"
          />
        </div>
        <div>
          <label className="label">電話番号</label>
          <input
            className="input"
            value={f.phone ?? ""}
            onChange={(e) => set("phone", e.target.value)}
            maxLength={40}
            inputMode="tel"
            placeholder="例: 09012345678"
          />
        </div>
        <div>
          <label className="label">事業内容</label>
          <textarea
            className="input min-h-[88px]"
            value={f.businessDescription ?? ""}
            onChange={(e) => set("businessDescription", e.target.value)}
            maxLength={2000}
            placeholder="事業の概要をご記入ください"
          />
        </div>
        <div>
          <label className="label">年商規模</label>
          <select
            className="input"
            value={f.annualRevenueBand ?? ""}
            onChange={(e) => set("annualRevenueBand", e.target.value)}
          >
            <option value="">選択しない</option>
            {REVENUE_OPTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">従業員数 / 業務委託人数</label>
          <select
            className="input"
            value={f.employeeBand ?? ""}
            onChange={(e) => set("employeeBand", e.target.value)}
          >
            <option value="">選択しない</option>
            {EMPLOYEE_OPTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">解決したい課題</label>
          <textarea
            className="input min-h-[88px]"
            value={f.challengesText ?? ""}
            onChange={(e) => set("challengesText", e.target.value)}
            maxLength={2000}
            placeholder="今いちばん解決したい課題は？"
          />
        </div>
        <div>
          <label className="label">{brand.shortName}に求める事</label>
          <textarea
            className="input min-h-[88px]"
            value={f.supportNeedsText ?? ""}
            onChange={(e) => set("supportNeedsText", e.target.value)}
            maxLength={2000}
            placeholder={`${brand.shortName}に期待すること・つながりたい相手など`}
          />
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button className="btn-primary btn-lg" disabled={busy}>
            {busy ? "保存中…" : "保存する"}
          </button>
          <Link to="/mypage" className="btn-outline">
            キャンセル
          </Link>
        </div>
      </form>
    </div>
  );
}
